/**
 * AuditLibraryPanel — the writer-facing view of a run's dynamic regulatory
 * audit library (precedent-aware audit).
 *
 * Renders the library's status, per-document processing progress, and the
 * acquired-source evidence list ("View source"). Reused in two places:
 *   - the manual-audit surface (DataGapsTabPanel), where it also gates the
 *     "Run review" button until every document is processed;
 *   - the library view, as an on-demand dropdown to browse the audit library.
 *
 * Presentational: it takes an already-fetched {@link RunAuditLibrary} and calls
 * back for refresh. Returns null when the feature is off or the run has no audit
 * library, so it is safe to mount unconditionally.
 */

import { Button, Link, makeStyles, mergeClasses, ProgressBar, Spinner, Textarea, tokens } from "@fluentui/react-components";
import { ArrowDownload16Regular } from "@fluentui/react-icons";
import type React from "react";
import { useState } from "react";
import { downloadBlob, fetchDocumentDownload, type RunAuditLibrary } from "../../../api/client";

const useStyles = makeStyles({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: "6px",
		padding: "8px",
		borderRadius: "6px",
		backgroundColor: tokens.colorNeutralBackground2,
	},
	header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" },
	headerActions: { display: "flex", alignItems: "center", gap: "4px" },
	title: { fontWeight: 600, fontSize: "12px" },
	statusRow: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px" },
	muted: { color: tokens.colorNeutralForeground3, fontSize: "11px" },
	docList: { margin: 0, paddingLeft: "16px", fontSize: "11px" },
	// Library-style document rows (mirrors how source-library docs read).
	docRows: { display: "flex", flexDirection: "column", gap: "2px", marginTop: "4px" },
	docRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: "8px",
		padding: "4px 6px",
		borderRadius: "4px",
		fontSize: "11px",
		backgroundColor: tokens.colorNeutralBackground1,
	},
	docName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
	docActions: { display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 },
	chip: {
		flexShrink: 0,
		padding: "1px 6px",
		borderRadius: "10px",
		fontSize: "10px",
		fontWeight: 600,
		display: "inline-flex",
		alignItems: "center",
		gap: "4px",
	},
	chipReady: { backgroundColor: tokens.colorPaletteGreenBackground2, color: tokens.colorPaletteGreenForeground2 },
	chipBusy: { backgroundColor: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground3 },
	chipFailed: { backgroundColor: tokens.colorPaletteRedBackground2, color: tokens.colorPaletteRedForeground1 },
	evidenceItem: { display: "flex", flexDirection: "column", gap: "1px", marginBottom: "4px" },
	evidenceMeta: { color: tokens.colorNeutralForeground3, fontSize: "10px" },
	failed: { color: tokens.colorPaletteRedForeground1, fontSize: "11px" },
	setupBox: { display: "flex", flexDirection: "column", gap: "6px" },
	setupActions: { display: "flex", alignItems: "center", gap: "6px" },
});

const _READY_DOC = new Set(["done", "ready", "parsed", "processed", "complete", "completed"]);
// Kept in sync with the server's _FAILED_STATUS (regulatory_audit_runner.py): a
// failed doc is terminal, so it never blocks the audit and reads as "Failed".
const _FAILED_DOC = new Set([
	"failed",
	"failed_upload",
	"failed_processing",
	"failed_parsing",
	"parse_failed",
	"parsing_failed",
	"error",
	"errored",
	"unsupported",
	"invalid",
	"cancelled",
	"canceled",
	"aborted",
]);

/** Latest per-document status → a short label + chip tone (library-style). */
function docStatusMeta(d: { parsed: boolean | null; status: string }): { label: string; tone: "ready" | "busy" | "failed" } {
	// Sangnan reports Title-Case, space-separated statuses ("Done", "Failed
	// parsing"); normalise to lower_snake so the status sets match regardless of
	// case/separator (kept in sync with the server's _normalize_status).
	const s = (d.status || "").toLowerCase().replace(/[ -]+/g, "_");
	if (d.parsed === true || _READY_DOC.has(s)) return { label: "Ready", tone: "ready" };
	if (_FAILED_DOC.has(s)) return { label: "Failed", tone: "failed" };
	return { label: d.status || "Processing", tone: "busy" };
}

/**
 * Whether a not-yet-ready audit library should block a manual review.
 *
 * Mirrors the server-side manual-audit gate (``POST …/sections/{id}/audit``
 * returns 409 ``audit_library_not_ready`` under the same condition): when the
 * precedent-aware feature is on and this run's audit library is still
 * assembling/processing (not ``ready``), the section audit is hard-blocked
 * until every document is parsed. Terminal states never block — ``empty`` and
 * ``failed`` fall back to a guidance-only audit, ``ready`` is good to go — and
 * the feature being off is a complete no-op (guidance-only, as before). A run
 * with no library yet also doesn't block here (the server runs guidance-only);
 * the panel's Build CTA surfaces the "build it first" step separately.
 */
export function auditLibraryBlocksAudit(data: RunAuditLibrary | null | undefined): boolean {
	if (!data || !data.enabled) return false;
	const lib = data.audit_library;
	if (!lib) return false;
	if (lib.status === "empty" || lib.status === "failed") return false;
	return !data.ready;
}

/** True when the audit library is present and fully processed. */
export function auditLibraryReady(data: RunAuditLibrary | null | undefined): boolean {
	return !!data && data.enabled && !!data.audit_library && data.ready;
}

function statusLabel(data: RunAuditLibrary, liveMessage?: string | null): string {
	const lib = data.audit_library;
	if (!lib) return "";
	const detail = (lib.stage_detail || "").trim();
	// A fresh SSE build line (temp-source bootstrap / acquisition phase) is more
	// current than the polled spine ``stage_detail``, and covers the temp-library
	// phase the poll can miss entirely — prefer it while a build is in flight.
	const live = (liveMessage || "").trim();
	switch (lib.status) {
		case "acquiring":
			// Prefer the live phase text ("Analyzing…"/"Searching…"/"Uploading…")
			// written by the builder; fall back to the static string.
			return live || detail || "Assembling audit library…";
		case "processing":
			return live || detail || `Processing ${data.processed}/${data.total} documents…`;
		case "ready":
			return `Ready · ${data.total} document${data.total === 1 ? "" : "s"}`;
		case "empty":
			return "No relevant precedent found";
		case "failed":
			return "Audit library unavailable";
		default:
			return lib.status;
	}
}

/**
 * Map a raw backend error string to a calm, accurate sentence. Some "errors" are
 * really just informational (a build that never finished on a prior run), and the
 * raw text otherwise read as an alarming red dump. The unmapped raw string is kept
 * so it can be tucked into a collapsed "Technical detail" rather than shown bare.
 */
function friendlyAuditError(raw: string): { message: string; tone: "info" | "error"; showRaw: boolean } {
	const s = (raw || "").trim();
	if (s.startsWith("Audit library assembly did not complete")) {
		return { message: "The audit library from this run didn’t finish. Rebuild it to try again.", tone: "info", showRaw: false };
	}
	if (s === "library creation failed") {
		return { message: "Couldn’t create the audit library — please Rebuild.", tone: "error", showRaw: false };
	}
	return { message: "Couldn’t finish building the audit library.", tone: "error", showRaw: true };
}

export interface AuditLibraryPanelProps {
	data: RunAuditLibrary | null | undefined;
	onRefresh?: () => void;
	/** Hide the per-document + evidence detail (compact status-only view). */
	compact?: boolean;
	/**
	 * Render nothing until the library is fully ready (usable precedent).
	 *
	 * Set on the Issues surfaces, where a not-available library (acquiring /
	 * processing / failed / empty) should simply not appear rather than surface a
	 * spinner or "unavailable" notice — including the stuck-``acquiring`` state a
	 * short run leaves behind when its child process exits mid-acquisition. Left
	 * off in the Library Hub, where the user intentionally opened the audit-library
	 * view and expects to see its live status.
	 *
	 * Superseded by ``allowSetup``: when the caller offers a manual setup CTA it
	 * renders that (not null) on the not-ready path.
	 */
	hideWhenNotReady?: boolean;
	/**
	 * Offer a manual "Set up audit library" CTA when it's not ready, and a
	 * "Rebuild" action (with an optional steering note) when it is. The parent
	 * owns the async call + polling; the panel just collects steering and fires
	 * ``onSetup``. Off by default (Library Hub stays a pure browse view).
	 */
	allowSetup?: boolean;
	/** Fire a (re)build with the user's optional steering note. */
	onSetup?: (steering: string) => void;
	/** Parent-driven: a build was just requested and is starting. */
	settingUp?: boolean;
	/** Parent-driven: last setup attempt's error, shown inline. */
	setupError?: string;
	/**
	 * Live build-phase line from the run's ``audit_library_*`` SSE stream
	 * (reviewStore's build-phase store). Present only while a build is in flight;
	 * preferred over the polled ``stage_detail`` so the multi-minute build — in
	 * particular the custom-submission temp-library bootstrap, which has no
	 * documents to show — narrates live instead of reading as a hang.
	 */
	liveBuildMessage?: string | null;
}

export function AuditLibraryPanel({
	data,
	onRefresh,
	compact,
	hideWhenNotReady,
	allowSetup,
	onSetup,
	settingUp,
	setupError,
	liveBuildMessage,
}: AuditLibraryPanelProps): React.ReactElement | null {
	const styles = useStyles();
	const [steering, setSteering] = useState("");
	const [rebuildOpen, setRebuildOpen] = useState(false);
	const [downloadingIds, setDownloadingIds] = useState<ReadonlySet<string>>(() => new Set());

	const handleDownloadDoc = async (docId: string): Promise<void> => {
		if (!docId || downloadingIds.has(docId)) return;
		setDownloadingIds((prev) => new Set(prev).add(docId));
		try {
			const { blob, filename } = await fetchDocumentDownload(docId);
			downloadBlob(blob, filename);
		} catch (error) {
			console.error("Failed to download audit-library document:", error);
		} finally {
			setDownloadingIds((prev) => {
				const next = new Set(prev);
				next.delete(docId);
				return next;
			});
		}
	};

	if (!data || !data.enabled) return null;

	const lib = data.audit_library;
	const ready = data.ready;
	// A live SSE build line means a build is running right now, even in the brief
	// window before the (6s) poll catches up to ``acquiring`` — so treat it as
	// in-progress to drive the spinner/status while the temp library is built.
	const live = (liveBuildMessage || "").trim() || null;
	const inProgress = !!settingUp || lib?.status === "acquiring" || lib?.status === "processing" || (!ready && !!live);

	// Determinate progress bar for the current build stage (candidates checked or
	// documents added, out of a known total). Rendered only while a build is in
	// flight and a total is known; the stage_detail sentence names the stage.
	const renderProgress = (): React.ReactElement | null => {
		const total = lib?.progress_total ?? 0;
		if (!total || total <= 0) return null;
		const current = Math.min(Math.max(lib?.progress_current ?? 0, 0), total);
		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }} data-testid="audit-library-progress">
				<ProgressBar value={current} max={total} thickness="medium" />
				<span className={styles.muted}>
					{current} / {total}
				</span>
			</div>
		);
	};

	// A steering textarea + primary action, reused by the not-ready CTA and the
	// ready-state Rebuild control. `label` is the button text when idle.
	const setupControls = (label: string): React.ReactElement => (
		<div className={styles.setupBox}>
			<Textarea
				size="small"
				value={steering}
				placeholder="Optional: Share any steering comments on info you want the agent to scrape for the audit library"
				onChange={(_e, d) => setSteering(d.value)}
				disabled={inProgress}
				resize="vertical"
				data-testid="audit-library-steering"
			/>
			<div className={styles.setupActions}>
				<Button
					size="small"
					appearance="primary"
					disabled={inProgress || !onSetup}
					onClick={() => {
						onSetup?.(steering.trim());
						// Collapse the steering box + this button the moment a (re)build
						// starts; the live phase text below takes over.
						setRebuildOpen(false);
						setSteering("");
					}}
					data-testid="audit-library-setup-btn"
				>
					{inProgress ? "Setting up…" : label}
				</Button>
				{inProgress ? <Spinner size="tiny" /> : null}
			</div>
			{setupError ? <div className={styles.failed}>{setupError}</div> : null}
		</div>
	);

	// --- Not-ready path -----------------------------------------------------
	// Once a build exists and has documents, fall through to the full panel so
	// the user sees every document + its latest status (like the source-library
	// view) instead of a hanging spinner. Only show the Build CTA when there is
	// nothing to show yet.
	const hasDocs = data.documents.length > 0;
	if (!ready) {
		if (allowSetup && !hasDocs) {
			// No library / no docs yet — a calm "not built yet" + a Build action
			// (no red error styling; a not-ready library isn't a failure).
			const statusText = inProgress
				? live || lib?.stage_detail?.trim() || "Building audit library…"
				: "Audit library not built yet.";
			return (
				<div className={styles.root} data-testid="audit-library-panel">
					<div className={styles.header}>
						<span className={styles.title}>Audit library</span>
					</div>
					<div className={styles.statusRow}>
						{inProgress ? <Spinner size="tiny" /> : null}
						<span className={styles.muted}>{statusText}</span>
					</div>
					{inProgress ? renderProgress() : null}
					{/* While a build is running, the box is replaced by the live status. */}
					{inProgress ? null : setupControls("Build audit library")}
				</div>
			);
		}
		if (!allowSetup && (!lib || hideWhenNotReady)) return null;
		// else: fall through to the full panel (documents + statuses shown below).
	}

	if (!lib) return null;
	const busy = lib.status === "acquiring" || lib.status === "processing";
	const failed = lib.status === "failed";

	return (
		<div className={styles.root} data-testid="audit-library-panel">
			<div className={styles.header}>
				<span className={styles.title}>Audit library</span>
				<div className={styles.headerActions}>
					{allowSetup && ready ? (
						<Button
							size="small"
							appearance="subtle"
							disabled={inProgress}
							onClick={() => setRebuildOpen((v) => !v)}
							data-testid="audit-library-rebuild-toggle"
						>
							Rebuild
						</Button>
					) : null}
					{onRefresh ? (
						<Button size="small" appearance="subtle" onClick={onRefresh} disabled={busy && !data.total}>
							Refresh
						</Button>
					) : null}
				</div>
			</div>

			<div className={styles.statusRow}>
				{busy || inProgress ? <Spinner size="tiny" /> : null}
				{/* A genuine acquiring/processing status uses statusLabel (live phase
				    text). Only when the rebuild latch is held but the poll still shows
				    a stale terminal (e.g. the old "unavailable" from before the rebuild
				    reached the backend) do we show a neutral "Rebuilding…" and suppress
				    the stale error, until the new build settles. */}
				<span className={failed && !inProgress ? styles.failed : undefined}>
					{busy || !inProgress ? statusLabel(data, live) : "Rebuilding audit library…"}
				</span>
			</div>
			{busy || inProgress ? renderProgress() : null}
			{lib.error && !inProgress
				? (() => {
						const fe = friendlyAuditError(lib.error);
						return (
							<div className={fe.tone === "error" ? styles.failed : styles.muted} data-testid="audit-library-error">
								{fe.message}
								{fe.showRaw ? (
									<details>
										<summary className={styles.muted}>Technical detail</summary>
										<span className={styles.muted}>{lib.error}</span>
									</details>
								) : null}
							</div>
						);
					})()
				: null}
			{lib.steering && !inProgress ? <div className={styles.muted}>Focus: {lib.steering}</div> : null}
			{allowSetup && ready && rebuildOpen && !inProgress ? setupControls("Rebuild") : null}

			{!compact && data.documents.length > 0 ? (
				<details>
					<summary className={styles.muted}>Documents ({data.processed}/{data.total} ready)</summary>
					<div className={styles.docRows}>
						{data.documents.map((d) => {
							const meta = docStatusMeta(d);
							const chipCls =
								meta.tone === "ready"
									? styles.chipReady
									: meta.tone === "failed"
										? styles.chipFailed
										: styles.chipBusy;
							return (
								<div className={styles.docRow} key={d.id || d.name} data-testid="audit-library-doc">
									<span className={styles.docName} title={d.name || d.id}>
										{d.name || d.id}
									</span>
									<span className={styles.docActions}>
										<span className={mergeClasses(styles.chip, chipCls)}>{meta.label}</span>
										{d.id ? (
											<Button
												size="small"
												appearance="subtle"
												icon={<ArrowDownload16Regular />}
												disabled={downloadingIds.has(d.id)}
												onClick={() => void handleDownloadDoc(d.id)}
												aria-label={`Download ${d.name || d.id}`}
												title="Download this document"
												data-testid="audit-library-doc-download"
											/>
										) : null}
									</span>
								</div>
							);
						})}
					</div>
				</details>
			) : null}

			{!compact && data.evidence.length > 0 ? (
				<details>
					<summary className={styles.muted}>Sources ({data.evidence.length})</summary>
					<div>
						{data.evidence.map((e, i) => (
							<div className={styles.evidenceItem} key={`${e.document_id || e.title}-${i}`}>
								{e.source_url ? (
									<Link href={e.source_url} target="_blank" rel="noreferrer">
										{e.title || e.source_url}
									</Link>
								) : (
									<span>{e.title || "(untitled source)"}</span>
								)}
								<span className={styles.evidenceMeta}>
									{[e.authority, e.source_type, e.evidence_class].filter(Boolean).join(" · ")}
								</span>
							</div>
						))}
					</div>
				</details>
			) : null}
		</div>
	);
}

export default AuditLibraryPanel;
